import { useCallback } from 'react';
import { LoaderCircle } from 'lucide-react';
import { ReadBuyer } from '@tinct/core-api-models';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import {
  useBuyerLogoVariant,
  useSaveBuyerLogo,
  useUploadBuyerLogoModal,
} from './hooks';
import { CheckingStep, SelectFileStep, UploadedStep } from './steps';

type UploadBuyerLogoModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyer: ReadBuyer | null;
  /** Which logo variant to upload — mirrors the block's display toggles. */
  greyscale?: boolean;
  dark?: boolean;
  onSuccess?: (buyer: ReadBuyer) => void;
};

/**
 * Upload/replace a buyer's brandmark logo variant. The variant (color,
 * greyscale, dark, greyscale-dark) follows the current block selection, so the
 * user always uploads the version the banner is missing. Files are validated
 * client-side (format, weight, dimensions) before the PATCH is enabled.
 */
export const UploadBuyerLogoModal = ({
  open,
  onOpenChange,
  buyer,
  greyscale = false,
  dark = false,
  onSuccess,
}: UploadBuyerLogoModalProps) => {
  const {
    variantKey,
    currentLogoUrl,
    isReplacing,
    companyName,
    versionLabel,
    exampleLogo,
  } = useBuyerLogoVariant({ buyer, greyscale, dark });

  const {
    step,
    file,
    fileDataUrl,
    checks,
    errorMessage,
    requirementLabels,
    handleFile,
    reset,
  } = useUploadBuyerLogoModal({ versionLabel });

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [onOpenChange, reset],
  );

  const handleSaved = useCallback(
    (updatedBuyer: ReadBuyer) => {
      handleOpenChange(false);
      onSuccess?.(updatedBuyer);
    },
    [handleOpenChange, onSuccess],
  );

  const { handleSave, isSaving } = useSaveBuyerLogo({
    buyer,
    file,
    fileDataUrl,
    variantKey,
    isReplacing,
    companyName,
    onSaved: handleSaved,
  });

  const isUploaded = step === 'uploaded';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[576px] rounded-2xl p-8">
        <DialogHeader>
          <DialogTitle>
            {isUploaded
              ? 'Logo uploaded!'
              : isReplacing
                ? 'Replace logo'
                : 'Add logo'}
          </DialogTitle>
          {!isUploaded && (
            <DialogDescription>
              Upload a logo for{' '}
              <span className="font-medium text-foreground">{companyName}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {(step === 'select' || step === 'error') && (
            <SelectFileStep
              step={step}
              errorMessage={errorMessage}
              checks={checks}
              requirementLabels={requirementLabels}
              versionLabel={versionLabel}
              exampleLogo={exampleLogo}
              dark={dark}
              onFile={handleFile}
            />
          )}

          {step === 'checking' && <CheckingStep />}

          {isUploaded && fileDataUrl && (
            <UploadedStep
              fileDataUrl={fileDataUrl}
              currentLogoUrl={currentLogoUrl}
              isReplacing={isReplacing}
              companyName={companyName}
            />
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isUploaded || isSaving}
          >
            {isSaving && <LoaderCircle className="size-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
